import { useMemo } from "react";
import { Link } from "react-router-dom";
//styles and animations
import { motion } from "framer-motion";
import {
  StyledMain,
  StyledMainContainer,
  StyledOffer,
  StyledOfferContainer,
  StyledConsultation,
  StyledConsultationContainer,
  StyledPrinciples,
  StyledPrinciplesContainer,
  StyledPrinciplesList,
  StyledPrinciplesListContainer,
  StyledTrust,
  StyledTrustContainer,
} from "../styles/pages";
import {
  MainPageAnimation,
  FadeInAnimation,
  FadeUpAnimation,
  FadeDownAnimation,
  FadeRightAnimation,
  FadeLeftAnimation,
} from "../styles/Animations";
import { AnimateInView } from "../components/AnimateInView";
//images
import { coordination, folder, shield } from "../img";
//files
import { consentPL, consentRU } from "../files";
//components
import Button from "../components/Button";
import Logo from "../components/Logo";
import BackToTopButton from "../components/BackToTopButton";
import ScrollOnNavigation from "../components/ScrollOnNavigation";
//constants
import { openCalendly } from "../utils/analytics";
import { plId, rusId } from "../constants/reviewWidgetIds";
//language
import { useLanguage } from "../hooks/useLanguage";
import type { LanguageProps } from "../typings";

const Home = ({ language }: LanguageProps) => {
  const lang = useLanguage(language);
  const widgetId = useMemo(
    () => (language === "Polish" ? plId : rusId),
    [language],
  );

  //scroll-triggered animations
  const [offerElement, offerControls] = AnimateInView();
  const [consultationElement, consultationControls] = AnimateInView();
  const [principlesElement, principlesControls] = AnimateInView();

  return (
    <motion.div
      variants={MainPageAnimation}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <StyledMain>
        <BackToTopButton />
        <StyledMainContainer>
          <Logo />
          <h1 className="hero-title">{lang.index.heroTitle}</h1>
          <p className="hero-subtitle">
            {lang.index.heroSubtitleLine1}
            <br />
            {lang.index.heroSubtitleLine2}
          </p>
        </StyledMainContainer>

        <StyledOffer>
          <StyledOfferContainer
            ref={offerElement}
            variants={FadeInAnimation}
            animate={offerControls}
          >
            <motion.div className="card" variants={FadeRightAnimation}>
              <img src={folder} alt="Documents" />
              <h3>{lang.index.offerDocsHeader}</h3>
              <p>{lang.index.offerDocsText}</p>
              <div className="links">
                <Link to="/services#other">{lang.index.offerDocsLink}</Link>
              </div>
            </motion.div>

            <motion.div className="card scaled" variants={FadeUpAnimation}>
              <img src={shield} alt="Legalization" />
              <h3>{lang.index.offerLegalizationHeader}</h3>
              <p>{lang.index.offerLegalizationText}</p>
              <div className="links">
                <Link to="/services#residence">
                  {lang.index.offerLegalizationLink1}
                </Link>
                <Link to="/services#citizenship">
                  {lang.index.offerLegalizationLink2}
                </Link>
              </div>
            </motion.div>

            <motion.div className="card" variants={FadeLeftAnimation}>
              <img src={coordination} alt="Support" />
              <h3>{lang.index.offerSupportHeader}</h3>
              <p>{lang.index.offerSupportText}</p>
              <div className="links">
                <Link to="/services#other">{lang.index.offerSupportLink}</Link>
              </div>
            </motion.div>
          </StyledOfferContainer>
        </StyledOffer>
      </StyledMain>

      <StyledConsultation>
        <StyledConsultationContainer
          ref={consultationElement}
          variants={FadeInAnimation}
          animate={consultationControls}
        >
          <motion.h2 variants={FadeUpAnimation}>
            {lang.index.consultationTitle}
          </motion.h2>
          <motion.p className="subtitle" variants={FadeUpAnimation}>
            {lang.index.consultationSubtitle}
          </motion.p>
          <motion.ul variants={FadeUpAnimation}>
            {lang.index.consultationList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </motion.ul>
          <motion.p className="note" variants={FadeUpAnimation}>
            💬 {lang.index.consultationNote}
          </motion.p>
          <motion.div variants={FadeUpAnimation}>
            <Button
              value={lang.index.consultationButton}
              onClick={() => openCalendly("home_consultation")}
            />
          </motion.div>
          <motion.div className="consent" variants={FadeUpAnimation}>
            {lang.index.formConsentLink}
            <Link
              to={language === "Polish" ? consentPL : consentRU}
              target="blank"
            >
              {lang.index.formConsentLinkText}
            </Link>
            .
          </motion.div>
        </StyledConsultationContainer>
      </StyledConsultation>

      <StyledPrinciples>
        <StyledPrinciplesContainer>
          <div className="intro">
            <h2>{lang.index.principlesTitle}</h2>
            <motion.div
              className="principles-content"
              ref={principlesElement}
              variants={FadeInAnimation}
              animate={principlesControls}
            >
              <motion.div className="stats" variants={FadeRightAnimation}>
                {lang.index.principlesStats.map((stat) => (
                  <div className="stat" key={stat.value}>
                    <strong>{stat.value}</strong>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div className="story" variants={FadeLeftAnimation}>
                <p className="lead">{lang.index.principlesIntroLead}</p>
                <p>{lang.index.principlesChallengesIntro}</p>
                <ul>
                  {lang.index.principlesChallenges.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>{lang.index.principlesChallengesOutro}</p>
                <p>{lang.index.principlesGoalsIntro}</p>
                <ul>
                  {lang.index.principlesGoals.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
            <motion.p
              className="section-closing-note"
              variants={FadeUpAnimation}
              animate={principlesControls}
            >
              {lang.index.principlesExperience2}
            </motion.p>
          </div>
        </StyledPrinciplesContainer>
      </StyledPrinciples>

      <StyledPrinciplesList>
        <StyledPrinciplesListContainer>
          <h2>{lang.index.principlesTitle2}</h2>
          <motion.div
            className="principles"
            variants={FadeInAnimation}
            animate={principlesControls}
          >
            <motion.div className="first" variants={FadeRightAnimation}>
              <span>1</span>
              <h3>{lang.index.principlesFirstHeader}</h3>
              <p>{lang.index.principlesFirstParagraph}</p>
            </motion.div>
            <motion.div className="second" variants={FadeDownAnimation}>
              <span>2</span>
              <h3>{lang.index.principlesSecondHeader}</h3>
              <p>{lang.index.principlesSecondParagraph}</p>
            </motion.div>
            <motion.div className="third" variants={FadeLeftAnimation}>
              <span>3</span>
              <h3>{lang.index.principlesThirdHeader}</h3>
              <p>{lang.index.principlesThirdParagraph}</p>
            </motion.div>
          </motion.div>
        </StyledPrinciplesListContainer>
      </StyledPrinciplesList>

      <StyledTrust>
        <StyledTrustContainer>
          <h2>{lang.index.trustTitle}</h2>
          <div className={widgetId} data-elfsight-app-lazy></div>
        </StyledTrustContainer>
      </StyledTrust>

      <ScrollOnNavigation />
    </motion.div>
  );
};

export default Home;
